import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";
import {
  IEligibleVendor,
  IPurchaseIndent,
  IPurchaseIndentApi,
  IPurchaseIndentUpload,
} from "common/interface/IPurchaseIndent";
import { logoutIfNeeded } from "common/utils";
import {
  getPersistedProcessingPurchaseIndent,
  persistProcessingPurchaseIndent,
  removePersistedProcessingPurchaseIndent,
} from "services/PersistService/PersistPurchaseIndentService";
import {
  getPersistedProcessingVendorAssignment,
  persistProcessingVendorAssignment,
  removePersistedProcessingVendorAssignment,
} from "services/PersistService/PersistVendorAssignmentService";
import { RootState } from "store/store";

export interface PurchaseIndentState {
  processingPurchaseIndent: IPurchaseIndentUpload;
  processingVendorAssignment: IPurchaseIndentUpload;
  indents?: IPurchaseIndentApi;
  eligibleVendors: IEligibleVendor[];
  status: "loading" | "success" | "error" | "idle" | "cancelling" | "saving";
  error?: string;
}

const initialState: PurchaseIndentState = {
  processingPurchaseIndent: getPersistedProcessingPurchaseIndent(),
  processingVendorAssignment: getPersistedProcessingVendorAssignment(),
  eligibleVendors: [],
  status: "idle",
};

export const saveProcessingPurchaseIndent = createAsyncThunk<IPurchaseIndentUpload>(
  "purchaseIndent/upload/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.purchaseIndent.processingPurchaseIndent;

    const response = await API.post(
      apis.savePurchaseIndent,
      {},
      { params: { key } }
    )
      .then((success) => success.data)
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

export const cancelProcessingPurchaseIndent = createAsyncThunk(
  "purchaseIndent/upload/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.purchaseIndent.processingPurchaseIndent;

    const response = await API.post(
      apis.savePurchaseIndent,
      {},
      { params: { key, cancel: 1 } }
    )
      .then((success) => success.data)
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

export const getAllPurchaseIndents = createAsyncThunk<
  IPurchaseIndentApi,
  { pageNo: number; pageSize: number; filter?: string }
>("purchaseIndent/all", async ({ pageNo = 1, pageSize = 25, filter = "" }, thunkApi) => {
  const filterParams = filter ? Object.fromEntries(new URLSearchParams(filter)) : {};
  const response = await API.get(apis.purchaseIndents, {
    params: { pageNo, pageSize, ...filterParams },
  })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const getPurchaseIndentById = createAsyncThunk<IPurchaseIndent, number>(
  "purchaseIndent/get",
  async (id, thunkApi) => {
    const response = await API.get(`${apis.purchaseIndents}/${id}`)
      .then((success) => success.data)
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

export const getEligibleVendors = createAsyncThunk<
  IEligibleVendor[],
  { purchaseSkuCode: string }
>("purchaseIndent/eligibleVendors", async ({ purchaseSkuCode }, thunkApi) => {
  const response = await API.get(apis.vendorSkuMapping, {
    params: { purchaseSkuCode },
  })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const assignVendorToIndent = createAsyncThunk<
  IPurchaseIndent,
  { id: number; vendorId: number; qty: number; poRate: number }
>("purchaseIndent/assign", async ({ id, vendorId, qty, poRate }, thunkApi) => {
  const response = await API.post(`${apis.purchaseIndents}/${id}/assign`, {
    vendorId,
    qty,
    poRate,
  })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const removeVendorFromIndent = createAsyncThunk<
  IPurchaseIndent,
  { id: number; vendorId: number }
>("purchaseIndent/removeAssign", async ({ id, vendorId }, thunkApi) => {
  const response = await API.delete(`${apis.purchaseIndents}/${id}/assign`, {
    params: { vendorId },
  })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const updatePurchaseIndentQty = createAsyncThunk<
  IPurchaseIndent,
  { id: number; qty: number }
>("purchaseIndent/updateQty", async ({ id, qty }, thunkApi) => {
  const response = await API.patch(`${apis.purchaseIndents}/${id}`, { qty })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const createPurchaseIndent = createAsyncThunk<
  IPurchaseIndent,
  { purchaseSkuCode: string; qty: number }
>("purchaseIndent/create", async ({ purchaseSkuCode, qty }, thunkApi) => {
  const response = await API.post(apis.createNewPurchaseIndent, { purchaseSkuCode, qty })
    .then((success) => success.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    });

  return response;
});

export const saveProcessingVendorAssignment = createAsyncThunk<IPurchaseIndentUpload>(
  "purchaseIndent/vendorAssignment/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.purchaseIndent.processingVendorAssignment;

    const response = await API.post(
      apis.saveVendorAssignment,
      {},
      { params: { key } }
    )
      .then((success) => success.data)
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

export const cancelProcessingVendorAssignment = createAsyncThunk(
  "purchaseIndent/vendorAssignment/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.purchaseIndent.processingVendorAssignment;

    const response = await API.post(
      apis.saveVendorAssignment,
      {},
      { params: { key, cancel: 1 } }
    )
      .then((success) => success.data)
      .catch((error) => {
        const errorMessage = error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

const purchaseIndentSlice = createSlice({
  name: "purchaseIndent",
  initialState,
  reducers: {
    setProcessingPurchaseIndent: (state, action) => {
      state.status = "idle";
      state.error = "";
      state.processingPurchaseIndent = action.payload;
      state.processingPurchaseIndent.timeUploaded = new Date().toString();
      persistProcessingPurchaseIndent(state.processingPurchaseIndent);
    },
    removeProcessingPurchaseIndent: (state) => {
      state.processingPurchaseIndent = {};
      state.status = "idle";
      state.error = "";
      removePersistedProcessingPurchaseIndent();
    },
    clearEligibleVendors: (state) => {
      state.eligibleVendors = [];
    },
    resetPurchaseIndentStatus: (state) => {
      state.status = "idle";
      state.error = "";
    },
    setProcessingVendorAssignment: (state, action) => {
      state.status = "idle";
      state.error = "";
      state.processingVendorAssignment = action.payload;
      state.processingVendorAssignment.timeUploaded = new Date().toString();
      persistProcessingVendorAssignment(state.processingVendorAssignment);
    },
    removeProcessingVendorAssignment: (state) => {
      state.processingVendorAssignment = {};
      state.status = "idle";
      state.error = "";
      removePersistedProcessingVendorAssignment();
    },
  },
  extraReducers(builder) {
    builder
      .addCase(saveProcessingPurchaseIndent.pending, (state) => {
        state.error = "";
        state.status = "loading";
      })
      .addCase(saveProcessingPurchaseIndent.fulfilled, (state) => {
        state.status = "success";
        state.error = "";
        state.processingPurchaseIndent = {};
        removePersistedProcessingPurchaseIndent();
      })
      .addCase(saveProcessingPurchaseIndent.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(cancelProcessingPurchaseIndent.pending, (state) => {
        state.status = "cancelling";
      })
      .addCase(cancelProcessingPurchaseIndent.fulfilled, (state) => {
        state.status = "idle";
        state.error = "";
        state.processingPurchaseIndent = {};
        removePersistedProcessingPurchaseIndent();
      })
      .addCase(cancelProcessingPurchaseIndent.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(getAllPurchaseIndents.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllPurchaseIndents.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        state.indents = action.payload;
      })
      .addCase(getAllPurchaseIndents.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(getPurchaseIndentById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getPurchaseIndentById.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        if (!state.indents) {
          state.indents = { data: [], pageNo: 1, pageSize: 25, pages: 1, total: 0 };
        }
        const idx = state.indents.data.findIndex((i) => i.id === action.payload.id);
        if (idx > -1) {
          state.indents.data[idx] = action.payload;
        } else {
          state.indents.data.push(action.payload);
        }
      })
      .addCase(getPurchaseIndentById.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(getEligibleVendors.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getEligibleVendors.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        state.eligibleVendors = action.payload ?? [];
      })
      .addCase(getEligibleVendors.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(assignVendorToIndent.pending, (state) => {
        state.status = "saving";
      })
      .addCase(assignVendorToIndent.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        if (state.indents) {
          const idx = state.indents.data.findIndex((i) => i.id === action.payload.id);
          if (idx > -1) state.indents.data[idx] = action.payload;
        }
      })
      .addCase(assignVendorToIndent.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(removeVendorFromIndent.pending, (state) => {
        state.status = "saving";
      })
      .addCase(removeVendorFromIndent.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        if (state.indents) {
          const idx = state.indents.data.findIndex((i) => i.id === action.payload.id);
          if (idx > -1) state.indents.data[idx] = action.payload;
        }
      })
      .addCase(removeVendorFromIndent.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(updatePurchaseIndentQty.pending, (state) => {
        state.status = "saving";
      })
      .addCase(updatePurchaseIndentQty.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        if (state.indents) {
          const idx = state.indents.data.findIndex((i) => i.id === action.payload.id);
          if (idx > -1) state.indents.data[idx] = action.payload;
        }
      })
      .addCase(updatePurchaseIndentQty.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(createPurchaseIndent.pending, (state) => {
        state.status = "saving";
        state.error = "";
      })
      .addCase(createPurchaseIndent.fulfilled, (state) => {
        state.status = "success";
        state.error = "";
      })
      .addCase(createPurchaseIndent.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(saveProcessingVendorAssignment.pending, (state) => {
        state.error = "";
        state.status = "loading";
      })
      .addCase(saveProcessingVendorAssignment.fulfilled, (state) => {
        state.status = "success";
        state.error = "";
        state.processingVendorAssignment = {};
        removePersistedProcessingVendorAssignment();
      })
      .addCase(saveProcessingVendorAssignment.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      })

      .addCase(cancelProcessingVendorAssignment.pending, (state) => {
        state.status = "cancelling";
      })
      .addCase(cancelProcessingVendorAssignment.fulfilled, (state) => {
        state.status = "idle";
        state.error = "";
        state.processingVendorAssignment = {};
        removePersistedProcessingVendorAssignment();
      })
      .addCase(cancelProcessingVendorAssignment.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        if (error.message) state.error = error.message;
      });
  },
});

export const {
  setProcessingPurchaseIndent,
  removeProcessingPurchaseIndent,
  clearEligibleVendors,
  resetPurchaseIndentStatus,
  setProcessingVendorAssignment,
  removeProcessingVendorAssignment,
} = purchaseIndentSlice.actions;

export default purchaseIndentSlice.reducer;
